/**
 * One publish, and what the page says about it (spec 2026-10-23 §5; ruling
 * R17). Both tabs publish through `useAppExperience.publish`, which calls this,
 * so both tabs get the same words.
 *
 *   - success → the new version number;
 *   - 409 → the conflict (the hook then shows the banner and a toast);
 *   - anything else → plain words that say what is wrong and how to fix it
 *     (`publishErrorText`): never the raw server text, a URL or a developer word.
 *
 * Kept apart from the hook so the root jest can test it for both surfaces
 * without React or the router (tests/unit/admin/app-experience-publish-errors.test.ts).
 */
import { AdminApiError } from '@/providers/admin-api';
import { publishSurface, type PublishBody } from './api';
import type { ExperienceSurface } from './lib/contract';
import { publishErrorText } from './lib/error-words';

export const CONFLICT_TEXT = 'Someone published a newer version. Reload to see it.';

export type PublishOutcome =
  | { ok: true; version: number }
  | { ok: false; conflict: boolean; message: string };

export async function publishOutcome(apiUrl: string, surface: ExperienceSurface, body: PublishBody): Promise<PublishOutcome> {
  try {
    const { version } = await publishSurface(apiUrl, surface, body);
    return { ok: true, version };
  } catch (e) {
    if (e instanceof AdminApiError && e.status === 409) return { ok: false, conflict: true, message: CONFLICT_TEXT };
    return { ok: false, conflict: false, message: publishErrorText(e) };
  }
}
